import { FieldModel } from '@cibo/core'
import { LandThumbnail } from '@cibo/landmanager'
import { Box, Button, MenuItem, Select, Stack, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface TemplateEmptyStateProps {
  fields: FieldModel[]
  canEdit: boolean
  hasPractices: boolean
  onPrePopulate: (fieldId: string) => void
}

export const TemplateEmptyState = ({
  fields,
  canEdit,
  hasPractices,
  onPrePopulate,
}: TemplateEmptyStateProps) => {
  const { t } = useTranslation('@cibo/programs/TemplateEditorModal')
  const [prePopulateExpanded, setPrePopulateExpanded] = useState(false)
  const [prePopulateFieldId, setPrePopulateFieldId] = useState<string>('')

  const showPrePopulate = canEdit && !hasPractices && fields.length > 0

  useEffect(() => {
    if (!showPrePopulate) {
      setPrePopulateExpanded(false)
    }
  }, [showPrePopulate])

  const handlePrePopulateClick = () => {
    if (!prePopulateExpanded) {
      setPrePopulateExpanded(true)
      setPrePopulateFieldId(fields[0]?.id ?? '')
    }
  }

  const handlePrePopulateConfirm = () => {
    if (prePopulateFieldId) {
      onPrePopulate(prePopulateFieldId)
      setPrePopulateExpanded(false)
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1.5,
        minHeight: 160,
        m: 2,
        border: '1px dashed',
        borderColor: 'divider',
        borderRadius: 1,
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {t('emptyStateFieldSelect')}
      </Typography>
      {showPrePopulate && !prePopulateExpanded && (
        <Button
          variant="outlined"
          size="small"
          data-testid="template-pre-populate-button"
          onClick={handlePrePopulateClick}
        >
          {t('prePopulate')}
        </Button>
      )}
      {showPrePopulate && prePopulateExpanded && (
        <Stack spacing={1} alignItems="stretch" sx={{ width: '100%', maxWidth: 320, px: 1 }}>
          <Select
            size="small"
            fullWidth
            value={prePopulateFieldId}
            onChange={e => setPrePopulateFieldId(e.target.value)}
            data-testid="template-pre-populate-field-select"
          >
            {fields.map(f => (
              <MenuItem key={f.id} value={f.id}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <LandThumbnail field={f} width={32} height={32} />
                  <Stack>
                    <Typography>{f.name}</Typography>
                    <Typography variant="caption">{f.acres?.toFixed(1) ?? 0} acres</Typography>
                  </Stack>
                </Stack>
              </MenuItem>
            ))}
          </Select>
          <Button
            variant="outlined"
            size="small"
            disabled={!prePopulateFieldId}
            data-testid="template-pre-populate-confirm-button"
            onClick={handlePrePopulateConfirm}
          >
            {t('prePopulateConfirm')}
          </Button>
        </Stack>
      )}
    </Box>
  )
}
